/** The itinerary's App button rules (plan §5 "App button", C8; O10) — pure, over the
 *  itinerary's values: which behaviour acts (its own, else the operator's when it inherits),
 *  which of the card's fields that behaviour does not use, and the standing notes the card's
 *  fields carry from them (`RecordEditorConfig.fieldNoteOf`, in both modes: the draft's values
 *  in edit mode). */
import {
  CTA_BUTTON_BEHAVIOR,
  CTA_BUTTON_BEHAVIOR_LABELS,
  ctaNeedsDeparture,
  ctaNeedsTour,
  ctaUsesBookingUrl,
  type ItineraryDetail,
} from '@/api/itineraries';
import type { ItineraryRecordFieldKey } from '@/api/itineraryRecord';
import { PHONE_BOOKING_URL_HINT, PHONE_NOT_TEL_NOTE, telNumber } from '@/features/catalog/words';
import type { RecordEditorConfig } from '@/features/records/editor/types';

/** The button behaviour that acts: the itinerary's own, else the operator's (inherit). */
export function effectiveBehaviour(d: Pick<ItineraryDetail, 'cta_button_behavior' | 'operator_cta_default'>): number | null {
  return d.cta_button_behavior ?? d.operator_cta_default;
}

/** The App button's fields the acting behaviour does not read: they stay on the card — kept,
 *  dimmed, still editable — "Not used with <behaviour>" (plan §5). */
export function unusedCtaField(
  key: ItineraryRecordFieldKey,
  d: Pick<ItineraryDetail, 'cta_button_behavior' | 'operator_cta_default'>,
): string | null {
  const behaviour = effectiveBehaviour(d);
  if (behaviour === null) return null;
  const b = String(behaviour);
  const used =
    key === 'booking_url' ? ctaUsesBookingUrl(b) : key === 'cta_tour' ? ctaNeedsTour(b) : key === 'cta_tour_departure' ? ctaNeedsDeparture(b) : true;
  return used ? null : `Not used with ${CTA_BUTTON_BEHAVIOR_LABELS[behaviour] ?? `behaviour ${behaviour}`}`;
}

/** A stored or drafted behaviour (an integer, or null: inherit). */
export const asBehaviour = (v: unknown): number | null => (typeof v === 'number' ? v : null);

/** The App button card's standing notes, from the itinerary's values — the draft's in edit mode
 *  (`RecordEditorConfig.fieldNoteOf`): a field the behaviour does not use is kept, dimmed, "Not
 *  used with <behaviour>"; with the Phone behaviour the Booking URL holds the `tel:` link the
 *  button dials (anything else there: the caution, never a block — the server warns the same). */
export function itineraryFieldNote(
  detail: Pick<ItineraryDetail, 'id' | 'operator_cta_default'>,
): NonNullable<RecordEditorConfig['fieldNoteOf']> {
  const rec = `itinerary:${detail.id}`;
  return (r, field, valueOf) => {
    if (r !== rec || field.entity !== 'itinerary') return null;
    const key = field.key as ItineraryRecordFieldKey;
    if (key !== 'booking_url' && key !== 'cta_tour' && key !== 'cta_tour_departure') return null;
    const own = asBehaviour(valueOf('cta_button_behavior'));
    const unused = unusedCtaField(key, { cta_button_behavior: own, operator_cta_default: detail.operator_cta_default });
    if (unused) return { text: unused, unused: true };
    const acting = effectiveBehaviour({ cta_button_behavior: own, operator_cta_default: detail.operator_cta_default });
    if (key === 'booking_url' && acting === CTA_BUTTON_BEHAVIOR.PHONE) {
      const url = valueOf('booking_url');
      const held = typeof url === 'string' && url.trim() !== '';
      return held && !telNumber(url) ? { text: PHONE_NOT_TEL_NOTE, tone: 'caution' } : { text: PHONE_BOOKING_URL_HINT };
    }
    return null;
  };
}
